import { getStorage, setStorage } from './storage';
import { courseCategories, findCategoryConfig, normalizeCategory } from './courseCategories';

export const COMMISSION_STORAGE_KEY = 'educabolivia_commission_rates_v1';
export const DEFAULT_COMMISSION_RATE = 20;

const clampCommissionRate = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return DEFAULT_COMMISSION_RATE;
  }

  return Math.min(95, Math.max(DEFAULT_COMMISSION_RATE, numericValue));
};

export const getCategoryKey = (course) => {
  const categoryValue = typeof course === 'string' ? course : course?.category;
  const categoryConfig = findCategoryConfig(categoryValue || '');

  if (categoryConfig?.key) {
    return categoryConfig.key;
  }

  const normalizedValue = normalizeCategory(categoryValue || '');
  return normalizedValue || 'sin-categoria';
};

export const getCategoryLabel = (course) => {
  const categoryValue = typeof course === 'string' ? course : course?.category;
  const categoryConfig = findCategoryConfig(categoryValue || '');

  if (categoryConfig?.name) {
    return categoryConfig.name;
  }

  if (typeof course === 'string' && course.trim()) {
    return course;
  }

  return categoryValue || 'Sin categoria';
};

export const getCommissionSettings = () => {
  const storedSettings = getStorage(COMMISSION_STORAGE_KEY);
  const baseSettings = {};

  courseCategories.forEach((category) => {
    baseSettings[category.key] = DEFAULT_COMMISSION_RATE;
  });

  if (!storedSettings || typeof storedSettings !== 'object') {
    return baseSettings;
  }

  Object.entries(storedSettings).forEach(([key, value]) => {
    baseSettings[key] = clampCommissionRate(value);
  });

  return baseSettings;
};

export const saveCommissionSettings = (settings) => {
  const sanitizedSettings = {};

  Object.entries(settings || {}).forEach(([key, value]) => {
    sanitizedSettings[key] = clampCommissionRate(value);
  });

  setStorage(COMMISSION_STORAGE_KEY, sanitizedSettings);
  return sanitizedSettings;
};

export const getCommissionRateForCourse = (course, settings = getCommissionSettings()) => {
  const categoryKey = getCategoryKey(course);
  return clampCommissionRate(settings?.[categoryKey]);
};

export const getEstimatedStudentsForCourse = (course) => {
  const explicitStudents = Number(course?.students);
  if (Number.isFinite(explicitStudents) && explicitStudents >= 0) {
    return explicitStudents;
  }

  const lectureCount = (course?.sections || []).reduce(
    (sum, section) => sum + (section?.lectures?.length || 0),
    0
  );

  return lectureCount > 0 ? lectureCount * 9 : 12;
};

export const getCompletionRateForCourse = (course) => {
  const sectionCount = course?.sections?.length || 0;
  const lectureCount = (course?.sections || []).reduce(
    (sum, section) => sum + (section?.lectures?.length || 0),
    0
  );
  const publicationBonus = course?.status === 'published' ? 18 : 5;
  const structureBonus = Math.min(28, sectionCount * 4 + lectureCount * 2);

  return Math.min(92, Math.max(20, publicationBonus + structureBonus));
};

export const getCompletedStudentsForCourse = (course) => {
  const students = getEstimatedStudentsForCourse(course);
  const completionRate = getCompletionRateForCourse(course);
  return Math.round((students * completionRate) / 100);
};

export const getCommissionAmountForCourse = (course, settings = getCommissionSettings()) => {
  const completedStudents = getCompletedStudentsForCourse(course);
  const coursePrice = Number(course?.price || 0);
  const commissionRate = getCommissionRateForCourse(course, settings);
  const grossCompletedRevenue = completedStudents * coursePrice;

  return {
    categoryKey: getCategoryKey(course),
    categoryLabel: getCategoryLabel(course),
    estimatedStudents: getEstimatedStudentsForCourse(course),
    completedStudents,
    completionRate: getCompletionRateForCourse(course),
    coursePrice,
    grossCompletedRevenue,
    commissionRate,
    commissionAmount: grossCompletedRevenue * (commissionRate / 100)
  };
};

export const buildCommissionSummary = (courses = [], settings = getCommissionSettings()) => {
  const groupedSummary = {};

  courses.forEach((course) => {
    const courseSummary = getCommissionAmountForCourse(course, settings);
    const categoryKey = courseSummary.categoryKey;

    if (!groupedSummary[categoryKey]) {
      groupedSummary[categoryKey] = {
        categoryKey,
        categoryLabel: courseSummary.categoryLabel,
        courseCount: 0,
        estimatedStudents: 0,
        completedStudents: 0,
        grossCompletedRevenue: 0,
        commissionRate: courseSummary.commissionRate,
        commissionAmount: 0
      };
    }

    groupedSummary[categoryKey].courseCount += 1;
    groupedSummary[categoryKey].estimatedStudents += courseSummary.estimatedStudents;
    groupedSummary[categoryKey].completedStudents += courseSummary.completedStudents;
    groupedSummary[categoryKey].grossCompletedRevenue += courseSummary.grossCompletedRevenue;
    groupedSummary[categoryKey].commissionAmount += courseSummary.commissionAmount;
    groupedSummary[categoryKey].commissionRate = courseSummary.commissionRate;
  });

  return Object.values(groupedSummary).sort((left, right) =>
    left.categoryLabel.localeCompare(right.categoryLabel, 'es')
  );
};
